import { NetworkStatus } from "@apollo/client";
import { Col, Empty, notification, Row, Spin } from "antd";
import dayjs from "dayjs";
import { Form, Formik } from "formik";
import { get, isEmpty, orderBy, sortBy } from "lodash";
import omitDeep from "omit-deep-lodash";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { FiSearch } from "react-icons/fi";
import {
  MdAssignmentTurnedIn,
  MdOpenInNew,
  MdOutlineAssignmentTurnedIn,
  MdOutlineFolderSpecial,
  MdPeopleOutline,
  MdRefresh,
  MdSend,
  MdSpeed,
  MdTimeline,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import ContactCard from "screens/Private/Broker/Clients/Card";
import ActivitiesLog from "shared/components/ActivitiesLog";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import Header from "shared/components/Header";
import Date from "shared/components/Input/Date";
import Select from "shared/components/Select";
import Status from "shared/components/Status";
import { useLazyQuery, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import { COLORS } from "shared/style/colors";
import graphql from "utils/api/graphql";
import { CALLS } from "utils/api/graphql/queries/call";
import { TRACER } from "utils/api/graphql/queries/tracers";
import {
  ACTIVITY_TYPES,
  ALVIO_ACTIVITY_ACTIONS,
  DATE_FORMAT,
  KEYS,
  SCREENS,
  THEMES_PALETTE,
  TRACER_TYPES,
  USER_TYPES,
} from "utils/constants";
import { projectSourceWhere } from "utils/helpers/sources";
import { activitySortTime, resolveCallActivityDate } from "utils/helpers/date";
import mapUserActivityToBrokerActivity, {
  sourceUserActivityActions,
} from "./mapUserActivityToBrokerActivity";

const buildDashboardMetaFromCall = (call) => {
  const project =
    call.project?.id != null
      ? {
          id: call.project.id,
          ...(call.project.contact?.id
            ? { contact: { id: call.project.contact.id } }
            : {}),
        }
      : null;
  const contactId = call.contact?.id ?? call.project?.contact?.id ?? null;
  if (!get(project, "id") && !contactId) {
    return null;
  }
  return { project, contactId };
};

const contactUserFromCallRecord = (call) => {
  const user = call.contact?.user || call.project?.contact?.user;
  if (!user?.firstname && !user?.lastname) {
    return undefined;
  }
  return {
    firstname: user.firstname || "",
    lastname: user.lastname || "",
  };
};

const enrichDashboardActivityContactUser = (activity, projects) => {
  if (activity.contactUser?.firstname || activity.contactUser?.lastname) {
    return activity;
  }
  const projectId = get(activity, "dashboardMeta.project.id");
  if (projectId) {
    const project = projects.find(({ id }) => id === projectId);
    const user = project?.contact?.user;
    if (user?.firstname || user?.lastname) {
      return {
        ...activity,
        contactUser: {
          firstname: user.firstname || "",
          lastname: user.lastname || "",
        },
      };
    }
  }
  const contactId = activity.dashboardMeta?.contactId;
  if (contactId) {
    const project = projects.find((p) => p.contact?.id === contactId);
    const user = project?.contact?.user;
    if (user?.firstname || user?.lastname) {
      return {
        ...activity,
        contactUser: {
          firstname: user.firstname || "",
          lastname: user.lastname || "",
        },
      };
    }
  }
  return activity;
};

const DASHBOARD_ALVIO_EVENTS = [
  "REPLY_DETECTED",
  "RESPONSE_RECEIVED",
  "MEETING_BOOKED",
  "ESCALATION",
];

/** Parameterized filter for `userActivities` (API `createSqlFilter`). */
const dashboardUserActivitiesCustom = {
  OR: [
    {
      AND: [
        { action: "updateSubscription" },
        {
          jsonbPath: {
            column: "data",
            path: ["input", "data", "status"],
            op: "=",
            value: "SIGNED",
          },
        },
      ],
    },
    {
      AND: [
        { action: "updateSubscription" },
        {
          jsonbPath: {
            column: "data",
            path: ["input", "data", "status"],
            op: "=",
            value: "PENDING",
          },
        },
      ],
    },
    { action: "addQuote" },
    { action: "addQualification" },
    { action: "signDocuments" },
    { action: "documentsSigned" },
    { action: "electronicSignatureCompleted" },
    { action: "manualSignatureCompleted" },
    { action: "sendEmail" },
    { action: ALVIO_ACTIVITY_ACTIONS.SEND_FAILED },
    ...DASHBOARD_ALVIO_EVENTS.map((event) => ({
      AND: [
        { action: ALVIO_ACTIVITY_ACTIONS.EVENT_RECEIVED },
        {
          jsonbPath: { column: "data", path: ["event"], op: "=", value: event },
        },
      ],
    })),
    ...Array.from(sourceUserActivityActions, (action) => ({ action })),
  ],
};

const projectSourceConditions = (currentFilters) => {
  const sourceWhere = projectSourceWhere(get(currentFilters, "sources"), {
    scopedToProject: true,
  });
  return sourceWhere ? [sourceWhere] : [];
};

const Dashboard = () => {
  const { user } = useContext(StoreContext);
  const { t } = useTranslation(NAME_SPACES.HOME);
  const { t: tContact } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTACT);
  const navigate = useNavigate();
  const FILTER = t("FILTER", { returnObjects: true });
  const organizationId = get(user, "organization.id");
  const isSupervisor =
    get(user, "meta.type") === USER_TYPES.INTERNAL_SUPERVISOR.type;
  const [tracers, setTracers] = useState([]);
  const [projects, setProjects] = useState([]);
  const activitiesScrollRef = useRef(null);
  const [activityContactDrawer, setActivityContactDrawer] = useState({
    open: false,
    contactId: null,
    project: null,
  });
  const take = 10;

  const savedFilters = JSON.parse(
    localStorage.getItem(KEYS.DASHBOARD_FILTERS),
  ) || {
    process: undefined,
    insurancePlan: undefined,
    author: undefined,
    dateRange: undefined,
    sources: [],
  };
  if (!Array.isArray(savedFilters.sources)) savedFilters.sources = [];
  const [filters, setFilters] = useState(savedFilters);

  const [fetchQuotes, { data: quoteCountData, loading: quoteCountLoading }] =
    useLazyQuery(graphql.queries.COUNT_QUOTES);

  const [
    countSubscriptions,
    { data: subscriptionCount, loading: subscriptionsCountLoading },
  ] = useLazyQuery(graphql.queries.COUNT_SUBSCRIPTIONS);

  const [
    countInsuredPersons,
    { data: insuredPersonsCount, loading: insuredPersonsCountLoading },
  ] = useLazyQuery(graphql.queries.COUNT_SUBSCRIPTIONS_INSURED_PERSONS);

  const [
    fetchTakeoverMetrics,
    { data: takeoverMetricsData, loading: takeoverMetricsLoading },
  ] = useLazyQuery(graphql.queries.PROJECT_TAKEOVER_METRICS, {
    fetchPolicy: "network-only",
  });

  const [fetchPoleUsers, { data: poleUsersData }] = useLazyQuery(
    graphql.queries.USERS,
  );

  const [fetchProjects, { loading: projectsLoading }] = useLazyQuery(
    graphql.queries.PROJECTS_LIST,
    {
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        setProjects(data?.projectsList?.data || []);
      },
    },
  );

  const [fetchTracer] = useLazyQuery(TRACER, {
    fetchPolicy: "network-only",
  });

  const [fetchProject] = useLazyQuery(graphql.queries.PROJECT, {
    fetchPolicy: "network-only",
  });

  const poleIds = useMemo(
    () => get(user, "poles", []).map(({ id }) => id),
    [user],
  );

  useEffect(() => {
    fetchAllData(savedFilters);
    if (isSupervisor && poleIds.length > 0) {
      fetchPoleUsers({
        variables: {
          isIn: {
            poles: { id: poleIds },
          },
          where: {
            isActive: true,
            OR: [
              { meta: { type: USER_TYPES.INTERNAL_ADVISOR.type } },
              { meta: { type: USER_TYPES.INTERNAL_SUPERVISOR.type } },
            ],
          },
        },
      });
    }
  }, []);

  useEffect(() => {
    if (!isSupervisor) return;
    const users = get(poleUsersData, "users.data", []);
    if (users.length > 0) {
      const userIds = users.map(({ id }) => id);
      fetchTakeoverData(userIds, filters);
    }
  }, [poleUsersData]);

  const buildAuthorFilter = (filterAuthor, poleUserIds) => {
    if (!isEmpty(filterAuthor)) {
      return [filterAuthor];
    }
    if (isSupervisor && poleUserIds.length > 0) {
      return poleUserIds;
    }
    if (!isSupervisor) {
      return [get(user, "id")];
    }
    return [];
  };

  const fetchTakeoverData = (authorIds, currentFilters = {}) => {
    const effectiveAuthors = buildAuthorFilter(
      get(currentFilters, "author"),
      authorIds,
    );
    const [startDate, endDate] = get(currentFilters, "dateRange", []) || [];

    const sourceWhere = projectSourceWhere(get(currentFilters, "sources"));
    const takeoverWhere = {
      visible: true,
      ...(effectiveAuthors.length > 0
        ? {
            OR: effectiveAuthors.map((id) => ({
              author: { id },
            })),
          }
        : {}),
      ...(sourceWhere ? { AND: [sourceWhere] } : {}),
    };
    fetchTakeoverMetrics({
      variables: {
        where: takeoverWhere,
        gt: { createdDate: startDate },
        lt: { createdDate: endDate },
      },
    });
  };

  const fetchAllData = (currentFilters) => {
    fetchData(currentFilters);
    const poleUserIds = get(poleUsersData, "users.data", []).map(
      ({ id }) => id,
    );
    fetchTakeoverData(poleUserIds, currentFilters);
  };

  const fetchData = (currentFilters) => {
    const [startDate, endDate] = get(currentFilters, "dateRange", []) || [];

    const authorConditions = !isEmpty(currentFilters.author)
      ? [{ project: { author: { id: currentFilters.author } } }]
      : [];

    fetchQuotes({
      variables: {
        where: {
          AND: [
            ...(currentFilters.process
              ? [
                  {
                    project: {
                      statuses: {
                        status: {
                          process: { id: currentFilters.process },
                        },
                      },
                    },
                  },
                ]
              : []),
            ...(currentFilters.insurancePlan
              ? [
                  {
                    project: {
                      subscriptions: {
                        insurancePlan: {
                          id: currentFilters.insurancePlan,
                        },
                      },
                    },
                  },
                ]
              : []),
            ...authorConditions,
            ...projectSourceConditions(currentFilters),
          ],
        },
        gt: { createdDate: startDate },
        lt: { createdDate: endDate },
      },
    });

    const signedSubscriptionsVariables = {
      where: {
        AND: [
          { status: "SIGNED" },
          ...(currentFilters.process
            ? [
                {
                  project: {
                    statuses: {
                      status: {
                        process: { id: currentFilters.process },
                      },
                    },
                  },
                },
              ]
            : []),
          ...(currentFilters.insurancePlan
            ? [
                {
                  project: {
                    subscriptions: {
                      insurancePlan: {
                        id: currentFilters.insurancePlan,
                      },
                    },
                  },
                },
              ]
            : []),
          ...authorConditions,
          ...projectSourceConditions(currentFilters),
        ],
      },
      gt: { signedDate: startDate },
      lt: { signedDate: endDate },
    };

    countSubscriptions({ variables: signedSubscriptionsVariables });
    countInsuredPersons({ variables: signedSubscriptionsVariables });

    fetchProjects({
      variables: {
        take: 10,
        skip: 0,
        where: { visible: true },
        withSubscriptions: false,
        withContract: false,
        withProcess: false,
        withProcessStatuses: false,
        withTracers: false,
        withFlows: false,
        exclude: [
          "subscriptions",
          "contract",
          "process",
          "processStatuses",
          "flows",
          "tracers",
          "possibleStatuses",
          "conditions",
          "actions",
          "actionsConditions",
        ],
      },
    });
  };

  const handleFilterSubmit = (values) => {
    setFilters(values);
    localStorage.setItem(KEYS.DASHBOARD_FILTERS, JSON.stringify(values));
    fetchAllData(values);
  };
  const variables = useMemo(
    () => ({
      take,
      custom: dashboardUserActivitiesCustom,
    }),
    [take],
  );

  const {
    data: userActivitiesData,
    loading: userActivitiesLoading,
    fetchMore,
    refetch,
    networkStatus,
  } = useQuery(graphql.queries.USER_ACTIVITY, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const loadingMoreUserActivities = networkStatus === NetworkStatus.fetchMore;

  const userActivitiesCount = userActivitiesData?.userActivities?.count ?? 0;
  const userActivitiesLoaded =
    userActivitiesData?.userActivities?.data?.length ?? 0;
  const hasMoreUserActivities = userActivitiesLoaded < userActivitiesCount;

  const {
    data: callsData,
    loading: callsLoading,
    refetch: refetchCalls,
  } = useQuery(CALLS, {
    skip: !organizationId,
    variables: {
      where: { ownedBy: { id: organizationId } },
      orderBy: [{ updatedDate: "DESC" }],
      take: 40,
    },
  });

  const callActivitiesForDashboard = useMemo(
    () =>
      (callsData?.calls?.data || []).map((call) => ({
        activityType: ACTIVITY_TYPES.CALL,
        date: resolveCallActivityDate(call),
        callData: call,
        dashboardMeta: buildDashboardMetaFromCall(call),
        contactUser: contactUserFromCallRecord(call),
      })),
    [callsData],
  );

  const mergedDashboardActivities = useMemo(() => {
    const logs = userActivitiesData?.userActivities?.data || [];
    const mapped = logs.map(mapUserActivityToBrokerActivity);
    const combined = [...mapped, ...callActivitiesForDashboard];
    const enriched = combined.map((activity) =>
      enrichDashboardActivityContactUser(activity, projects),
    );
    return orderBy(
      enriched,
      (activity) => activitySortTime(activity.date),
      "desc",
    );
  }, [userActivitiesData, callActivitiesForDashboard, projects]);

  const loadMoreActivities = useCallback(() => {
    if (loadingMoreUserActivities) {
      return;
    }
    const newSkip = userActivitiesData?.userActivities?.data?.length ?? 0;
    fetchMore({
      variables: {
        ...variables,
        skip: newSkip,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.userActivities?.data?.length) {
          notification.info({ message: t("NO_MORE_ACTIVITIES") });
          return previousResult;
        }
        const existingIds = new Set(
          previousResult.userActivities.data.map((item) => item.id),
        );
        const newActivities = fetchMoreResult.userActivities.data.filter(
          (item) => !existingIds.has(item.id),
        );
        if (!newActivities.length) {
          notification.info({ message: t("NO_MORE_ACTIVITIES") });
          return previousResult;
        }
        return {
          ...previousResult,
          userActivities: {
            ...previousResult.userActivities,
            data: [...previousResult.userActivities.data, ...newActivities],
          },
        };
      },
    }).then(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = activitiesScrollRef.current;
          if (el) {
            el.scrollTo({
              top: el.scrollHeight,
              behavior: "smooth",
            });
          }
        });
      });
    });
  }, [
    fetchMore,
    loadingMoreUserActivities,
    t,
    userActivitiesData?.userActivities?.data?.length,
    variables,
  ]);

  const quoteCount = useMemo(
    () => quoteCountData?.countQuotes || 0,
    [quoteCountData],
  );

  const showActivityWarning = useCallback(() => {
    notification.warning({
      message: "Notification",
      description: t("NOTIFICATION_ACTIVITY"),
    });
  }, [t]);

  const navigateWithTracer = useCallback(
    (projectId, projectStatuses, fetchedTracer) => {
      const cleanTracer = omitDeep(fetchedTracer, "__typename");
      const { process } = cleanTracer.flow;
      const projectStatusIds = new Set(
        (projectStatuses || [])
          .filter(({ status }) => status)
          .map(({ status }) => status.id),
      );
      const currentStatus = sortBy(
        (process.processStatuses || []).filter(({ id }) =>
          projectStatusIds.has(id),
        ),
        ({ order }) => -parseInt(order, 10),
      )[0];

      const path = `${SCREENS.PRIVATE.BROKER.PROJECTS.DETAIL.path}/${projectId}`;
      navigate(path, {
        state: {
          process,
          tracer: cleanTracer,
          ...(currentStatus ? { status: currentStatus } : {}),
        },
      });
    },
    [navigate],
  );

  const resolveAndNavigate = useCallback(
    (projectData) => {
      const statuses = projectData.statuses || [];
      const processIds = [
        ...new Set(
          statuses
            .map(({ status }) => get(status, "process.id"))
            .filter(Boolean),
        ),
      ];

      if (!processIds.length) {
        showActivityWarning();
        return;
      }

      const tryProcessId = (index) => {
        if (index >= processIds.length) {
          showActivityWarning();
          return;
        }
        fetchTracer({
          variables: {
            where: {
              type: TRACER_TYPES.MANUAL,
              flow: { process: { id: processIds[index] } },
            },
          },
          onCompleted: ({ tracer: fetchedTracer }) => {
            if (fetchedTracer?.flow?.process) {
              navigateWithTracer(projectData.id, statuses, fetchedTracer);
            } else {
              tryProcessId(index + 1);
            }
          },
        });
      };

      tryProcessId(0);
    },
    [fetchTracer, navigateWithTracer, showActivityWarning],
  );

  const onDetail = useCallback(
    (project) => {
      if (!project || !project.id) {
        showActivityWarning();
        return;
      }

      fetchProject({
        variables: { where: { id: project.id } },
        onCompleted: ({ project: fullProject }) => {
          if (!fullProject) {
            showActivityWarning();
            return;
          }
          resolveAndNavigate(fullProject);
        },
      });
    },
    [fetchProject, resolveAndNavigate, showActivityWarning],
  );

  const handleOpenProjectFromActivity = useCallback(
    (activity) => {
      const projectId = get(activity, "dashboardMeta.project.id");
      if (!projectId) {
        showActivityWarning();
        return;
      }

      fetchProject({
        variables: { where: { id: projectId } },
        onCompleted: ({ project: fullProject }) => {
          if (!fullProject) {
            showActivityWarning();
            return;
          }
          resolveAndNavigate(fullProject);
        },
      });
    },
    [fetchProject, resolveAndNavigate, showActivityWarning],
  );

  const handleOpenContactFromActivity = useCallback((activity) => {
    const meta = activity.dashboardMeta;
    const projectId = get(meta, "project.id");
    const contactId = meta?.contactId || get(meta, "project.contact.id");
    if (!contactId) {
      return;
    }
    setActivityContactDrawer({
      open: true,
      contactId,
      project: projectId
        ? {
            id: projectId,
            contact: { id: contactId },
          }
        : undefined,
    });
  }, []);

  const activityLinks = useMemo(
    () => ({
      enabled: true,
      labels: {
        project: tContact("ACTIVITIES.OPEN_PROJECT"),
        contact: tContact("ACTIVITIES.OPEN_CONTACT"),
      },
      onOpenProject: handleOpenProjectFromActivity,
      onOpenContact: handleOpenContactFromActivity,
    }),
    [handleOpenContactFromActivity, handleOpenProjectFromActivity, tContact],
  );

  const setActivityContactDrawerOpen = useCallback((open) => {
    setActivityContactDrawer((prev) => ({ ...prev, open }));
  }, []);

  const handleRefresh = () => {
    fetchAllData(filters);
    refetch({
      variables,
    });
    if (organizationId) {
      refetchCalls();
    }
    notification.success({ message: t("NOTIFICATION_REFRESH") });
  };

  return (
    <>
      <Header
        title={t("DASHBOARD")}
        actions={[
          {
            icon:
              userActivitiesLoading || projectsLoading ? (
                <Spin size="small" />
              ) : (
                <MdRefresh size={20} />
              ),
            onClick: handleRefresh,
            type: "primary--outlined",
            loading:
              userActivitiesLoading ||
              callsLoading ||
              quoteCountLoading ||
              subscriptionsCountLoading ||
              insuredPersonsCountLoading ||
              projectsLoading ||
              takeoverMetricsLoading,
          },
        ]}
      />

      <Card type="simple">
        <Formik initialValues={filters} onSubmit={handleFilterSubmit}>
          {({ setFieldValue }) => (
            <Form>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8} lg={7}>
                  <Date
                    label={FILTER.PERIOD}
                    variant="range"
                    format={DATE_FORMAT}
                    onChange={(dates) => setFieldValue("dateRange", dates)}
                    disabledDate={(current) =>
                      current && current.isAfter(dayjs().endOf("day"))
                    }
                    defaultValue={
                      get(filters, "dateRange", [])?.length === 2
                        ? [
                            dayjs(get(filters, "dateRange.0")),
                            dayjs(get(filters, "dateRange.1")),
                          ]
                        : undefined
                    }
                  />
                </Col>
                {isSupervisor && (
                  <Col xs={24} sm={12} md={8} lg={7}>
                    <Select
                      type="async"
                      onChange={(value) => setFieldValue("author", value)}
                      label={FILTER.ADVISOR}
                      node={{
                        query: graphql.queries.USERS,
                        variables: {
                          isIn: {
                            ...(poleIds.length > 0
                              ? { poles: { id: poleIds } }
                              : {}),
                            organization: {
                              id: [organizationId],
                            },
                          },
                          where: {
                            isActive: true,
                            OR: [
                              {
                                meta: {
                                  type: USER_TYPES.INTERNAL_ADVISOR.type,
                                },
                              },
                              {
                                meta: {
                                  type: USER_TYPES.INTERNAL_SUPERVISOR.type,
                                },
                              },
                            ],
                          },
                        },
                        accessors: {
                          root: "users.data",
                          label: {
                            separator: " ",
                            keys: ["firstname", "lastname"],
                          },
                        },
                      }}
                      placeholder={FILTER.ADVISOR}
                      allowClear
                      defaultValue={filters.author}
                    />
                  </Col>
                )}
                <Col xs={24} sm={12} md={8} lg={7}>
                  <Select
                    type="async"
                    mode="multiple"
                    onChange={(value) => setFieldValue("sources", value)}
                    label={FILTER.SOURCES}
                    node={{
                      query: graphql.queries.SOURCES,
                      variables: { where: {} },
                      accessors: { root: "sources.data" },
                    }}
                    placeholder={FILTER.SELECT_SOURCES}
                    allowClear
                    defaultValue={filters.sources}
                  />
                </Col>
                <Col xs={24} sm={12} md={4} lg={3} className="p-top-24">
                  <Button
                    key="done"
                    icon={<FiSearch size={20} />}
                    type="primary"
                    size="full--width"
                    htmlType="submit"
                  >
                    {FILTER.FILTER_LABEL}
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </Card>

      <div className="p-top-20" />

      <Row gutter={[16, 16]} align={"bottom"}>
        <Col xs={24} sm={12} md={8} lg={8}>
          <Card
            type={"count"}
            icon={<MdOutlineFolderSpecial size={28} />}
            label={FILTER.NUMBER_OF_QUOTES}
            value={quoteCountLoading ? <Spin size="small" /> : quoteCount}
            onClick={() => navigate(SCREENS.PRIVATE.BROKER.PROJECTS.LIST.path)}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8}>
          <Card
            type="count"
            icon={<MdOutlineAssignmentTurnedIn size={28} />}
            label={FILTER.NUMBER_OF_CONTRACTS}
            onClick={() => navigate(SCREENS.PRIVATE.BROKER.CONTRACTS.LIST.path)}
            value={
              subscriptionsCountLoading ? (
                <Spin size="small" />
              ) : (
                get(subscriptionCount, "countSubscriptions")
              )
            }
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8}>
          <Card
            type="count"
            icon={<MdPeopleOutline size={28} />}
            label={FILTER.NUMBER_OF_INSURED_PERSONS}
            onClick={() => navigate(SCREENS.PRIVATE.BROKER.CONTRACTS.LIST.path)}
            value={
              insuredPersonsCountLoading ? (
                <Spin size="small" />
              ) : (
                get(insuredPersonsCount, "countSubscriptionsInsuredPersons")
              )
            }
          />
        </Col>
      </Row>

      <div className="p-top-20" />

      <Row gutter={[16, 16]} align={"bottom"}>
        <Col xs={12} sm={12} md={6} lg={6}>
          <Card
            type="count"
            icon={<MdSend size={28} />}
            label={FILTER.DISTRIBUTED_PROJECTS}
            value={
              takeoverMetricsLoading ? (
                <Spin size="small" />
              ) : (
                (get(
                  takeoverMetricsData,
                  "projectTakeoverMetrics.distributedCount",
                ) ?? "-")
              )
            }
          />
        </Col>
        <Col xs={12} sm={12} md={6} lg={6}>
          <Card
            type="count"
            icon={<MdAssignmentTurnedIn size={28} />}
            label={FILTER.TAKEN_OVER_PROJECTS}
            value={
              takeoverMetricsLoading ? (
                <Spin size="small" />
              ) : (
                (get(
                  takeoverMetricsData,
                  "projectTakeoverMetrics.takenOverCount",
                ) ?? "-")
              )
            }
          />
        </Col>
        <Col xs={12} sm={12} md={6} lg={6}>
          <Card
            type="count"
            icon={<MdSpeed size={28} />}
            label={FILTER.TAKEOVER_RATE}
            value={
              takeoverMetricsLoading ? (
                <Spin size="small" />
              ) : (
                `${get(takeoverMetricsData, "projectTakeoverMetrics.takeoverRate", 0)}%`
              )
            }
          />
        </Col>
        <Col xs={12} sm={12} md={6} lg={6}>
          <Card
            type="count"
            icon={<MdTimeline size={28} />}
            label={FILTER.AVG_TAKEOVER_DELAY}
            value={
              takeoverMetricsLoading ? (
                <Spin size="small" />
              ) : (
                (() => {
                  const ms = get(
                    takeoverMetricsData,
                    "projectTakeoverMetrics.averageTakeoverDelayMs",
                    0,
                  );
                  if (!ms) return "-";
                  const totalMinutes = Math.floor(ms / 60000);
                  const hours = Math.floor(totalMinutes / 60);
                  const minutes = totalMinutes % 60;
                  if (hours >= 24) {
                    const days = Math.floor(hours / 24);
                    const remainingHours = hours % 24;
                    if (remainingHours === 0) return `${days}j ${minutes}min`;
                    return `${days}j ${remainingHours}h ${minutes}min`;
                  }
                  if (hours === 0) return `${minutes}min`;
                  return `${hours}h ${minutes}min`;
                })()
              )
            }
          />
        </Col>
      </Row>

      <div className="p-top-20" />

      <Row gutter={[16, 16]} align={"top"}>
        <Col xs={24} sm={24} md={24} lg={16}>
          <Card type={"simple"} head title={t("ACTIVITIES_NAME")}>
            <div className="activities__area" ref={activitiesScrollRef}>
              {(userActivitiesLoading || callsLoading) &&
              !mergedDashboardActivities.length ? (
                <Spin size="small" />
              ) : !mergedDashboardActivities.length ? (
                <Empty />
              ) : (
                <ActivitiesLog
                  activities={mergedDashboardActivities}
                  hasMore={hasMoreUserActivities}
                  onLoadMore={loadMoreActivities}
                  loadingMore={loadingMoreUserActivities}
                  activityLinks={activityLinks}
                  loadMoreLabel={t("SHOW_MORE")}
                  loadMoreLoadingLabel={t("SHOW_MORE_LOADING")}
                />
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={24} md={24} lg={8}>
          <Card type={"simple"} head title={t("LATEST_PROJECTS")}>
            <div className="latset__projects">
              {projectsLoading ? (
                <Spin size="small" />
              ) : (
                projects.map((project) => (
                  <div className="latset__projects--item" key={project.id}>
                    <div className="info">
                      <div className="content">
                        <div className="name">
                          <span>
                            {project.contact?.user?.firstname}{" "}
                            {project.contact?.user?.lastname}
                          </span>
                          <Status
                            status="primary"
                            text={get(
                              get(
                                THEMES_PALETTE,
                                project?.fields.processTemplate,
                                "DEFAULT",
                              ),
                              "NAME",
                            )}
                          />
                        </div>
                        <small>{project.statuses?.[0]?.status?.name}</small>
                      </div>
                    </div>

                    <span onClick={() => onDetail(project)}>
                      <MdOpenInNew size={20} color={COLORS.C_PRIMARY} />
                    </span>
                  </div>
                ))
              )}
            </div>
            <div className="d-flex align--center justify--center">
              <Button
                onClick={() =>
                  navigate(SCREENS.PRIVATE.BROKER.PROJECTS.LIST.path)
                }
                type={"primary--underline"}
              >
                {t("SHOW_ALL")} <MdOpenInNew />
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      {activityContactDrawer.contactId ? (
        <ContactCard
          contactId={activityContactDrawer.contactId}
          project={activityContactDrawer.project}
          open={activityContactDrawer.open}
          setOpen={setActivityContactDrawerOpen}
          tracers={tracers}
          setTracers={setTracers}
        />
      ) : null}
    </>
  );
};

export default Dashboard;
